/**
 * Plane-neutral blocks that any document may compose.
 *
 * These carry no legal text, which is what lets them live outside legal
 * review: `common.custom-section` is the tenant-authored escape hatch
 * (ADR-0060's custom sections, expressed as a block) and `common.notice`
 * is a shared-plane advisory box.
 */
import { nodes, notice, p, section, t, val } from '@hyranai/regta-document-ir';
import { rows, str } from '../lib.js';
import { customSectionsBlock } from './custom-sections.js';
export const customSection = {
    blockKey: 'common.custom-section',
    version: 'v1',
    plane: 'tenant',
    status: 'published',
    language: 'sv-SE',
    title: 'Egen paragraf',
    summary: 'A user-authored numbered section: a title plus free-text paragraphs and optional key/value rows.',
    slots: [
        { key: 'ref', label: 'Referens', type: 'text', required: true, help: 'Stabil identitet, t.ex. "sarskilda-villkor".' },
        { key: 'title', label: 'Rubrik', type: 'text', required: true },
        { key: 'body', label: 'Text', type: 'textarea' },
        {
            key: 'rows',
            label: 'Uppgifter',
            type: 'table',
            repeats: true,
            columns: [
                { key: 'label', label: 'Etikett', type: 'text' },
                { key: 'value', label: 'Värde', type: 'text' },
            ],
        },
    ],
    compile: (input) => {
        const ref = str(input.ref);
        const title = str(input.title);
        if (!ref || !title)
            return null;
        const paragraphs = (str(input.body) ?? '')
            .split(/\n{2,}/)
            .map((chunk) => chunk.trim())
            .filter((chunk) => chunk !== '')
            .map((chunk) => p(chunk));
        const detailRows = rows(input.rows)
            .map((row) => [str(row.label), str(row.value)])
            .filter(([label]) => label != null);
        return [
            section(ref, title, [
                ...paragraphs,
                ...(detailRows.length
                    ? [
                        {
                            type: 'detailList',
                            items: detailRows.map(([label, value]) => ({
                                term: [t(label)],
                                description: [p([val(value)])],
                            })),
                        },
                    ]
                    : []),
            ]),
        ];
    },
};
export const noticeBlock = {
    blockKey: 'common.notice',
    version: 'v1',
    plane: 'shared',
    status: 'published',
    language: 'sv-SE',
    title: 'Upplysning',
    summary: 'A set-apart advisory box; informational, never operative legal text.',
    slots: [
        { key: 'title', label: 'Rubrik', type: 'text' },
        { key: 'body', label: 'Text', type: 'textarea', required: true },
        {
            key: 'tone',
            label: 'Ton',
            type: 'select',
            options: [
                { code: 'info', label: 'Information', summary: 'Neutral informational note.' },
                { code: 'warning', label: 'Varning', summary: 'A caution the reader should not miss.' },
                { code: 'legal', label: 'Rättslig upplysning', summary: 'A statutory advisory notice.' },
            ],
        },
    ],
    compile: (input) => {
        const body = str(input.body);
        if (!body)
            return null;
        const tone = input.tone === 'warning' || input.tone === 'legal' ? input.tone : 'info';
        return nodes(notice([p(body)], { tone, ...(str(input.title) ? { title: str(input.title) } : {}) }));
    },
};
export { customSectionsBlock };
export const COMMON_BLOCKS = [customSection, noticeBlock, customSectionsBlock];
//# sourceMappingURL=blocks.js.map